import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import Header from './Header';
import Sidebar from './Sidebar';
import MonitoringDashboard from './MonitoringDashboard';

interface LayoutProps {
  children: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(280);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 640);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <div
      className="flex min-h-screen"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        color: 'var(--text-primary)',
      }}
    >
      {/* Sidebar */}
      <Sidebar
        isOpen={isSidebarOpen}
        onToggle={toggleSidebar}
        width={sidebarWidth}
        onWidthChange={setSidebarWidth}
      />

      {/* Backdrop — mobile only, closes sidebar on tap */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 sm:hidden"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      {/* Main Content Area */}
      <div
        className="flex flex-col flex-1 min-w-0 transition-all duration-300"
        style={{
          marginLeft: isSidebarOpen && isDesktop ? `${sidebarWidth}px` : '0',
        }}
      >
        <Header onSidebarToggle={toggleSidebar} isSidebarOpen={isSidebarOpen} />
        <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8">{children}</main>
        {import.meta.env.VITE_ENABLE_PERFORMANCE_MONITORING === 'true' && (
          <MonitoringDashboard />
        )}
      </div>
    </div>
  );
};

export default Layout;
